import { getMessage } from '../../../../common/utils/MessageUtils'

/** Master CSV 取込1行を7列（第1〜第6階層セル＋組織コード）とみなしたときの「組織コード」列の0始まりインデックス。 画面の行エラー表示は列インデックスでセルに紐づけるため、組織コード列のエラーは常にこのインデックスで返す。 */
const HIERARCHY_ORG_CODE_COLUMN_INDEX = 6

/** 禁則文字: , \ " */
export const FORBIDDEN_CHARS = [',', '\\', '"']

/**
 * 組織追加・編集用バリデーション（単項目）- モーダル入力用
 *
 * @param {{
 *   department_code?: string
 *   department_name?: string
 *   department_short_name?: string
 *   segment_name?: string
 *   segment_code?: string
 * }} org
 *   - 組織オブジェクト（セグメント名は任意）
 *
 * @param {Set<string>} existingCodes - 既存の組織コード（重複チェック用）。追加時は除外するコードを渡さない
 * @param {string | null} [excludeCode] - 重複チェックから除外する組織コード（編集時）
 * @param {{ forImport?: boolean }} [options] - ForImport: true のときセル表示用短メッセージ（E02xxxx）を返す
 * @returns {{ [key: string]: string }} 項目名をキーにしたエラーメッセージ
 */
export function validateDepartment(org, existingCodes = new Set(), excludeCode = null, options = {}) {
  const errors = {}
  const forImport = options.forImport === true
  const code = (org.department_code ?? '').toString().trim()
  const name = (org.department_name ?? '').toString().trim()
  const shortName = (org.department_short_name ?? '').toString().trim()

  // 組織コード: E010001/E020001, E010016/E020002, E010013/E020003
  if (!code) {
    errors.department_code = forImport ? getMessage('E020001') : getMessage('E010001', '組織コード')
  } else if (FORBIDDEN_CHARS.some((c) => code.includes(c))) {
    errors.department_code = forImport ? getMessage('E020002') : getMessage('E010016', FORBIDDEN_CHARS.join(' '))
  } else if (existingCodes.has(code) && code !== excludeCode) {
    errors.department_code = forImport ? getMessage('E020003') : getMessage('E010013', '同一の「組織コード」')
  }

  // 組織名: E010001/E020001, E010016/E020002
  if (!name) {
    errors.department_name = forImport ? getMessage('E020001') : getMessage('E010001', '組織名')
  } else if (FORBIDDEN_CHARS.some((c) => name.includes(c))) {
    errors.department_name = forImport ? getMessage('E020002') : getMessage('E010016', FORBIDDEN_CHARS.join(' '))
  }

  // 組織略称: E010016/E020002
  if (shortName && FORBIDDEN_CHARS.some((c) => shortName.includes(c))) {
    errors.department_short_name = forImport ? getMessage('E020002') : getMessage('E010016', FORBIDDEN_CHARS.join(' '))
  }

  // セグメント名: 任意（未選択可）

  return errors
}

/**
 * 組織階層1行のバリデーション（インポート用・クロス検証用）。 master CSV（テーブル用・drilldown記号なし）を渡すことを前提とする。 登録済みデータがDynamoDBで4階層の場合は [lev1, lev2,
 * lev3, lev4, 組織コード] の5要素になる。
 *
 * @param {string[]} row - 第1〜第N階層＋組織コード（最低2要素: 1階層以上＋組織コード）。最大7要素（6階層＋コード）
 * @param {Object[]} departmentList - 組織一覧（department_code, department_name 等）
 * @param {{ departmentNameFormat?: string }} [options] - 現在未使用（将来の拡張用に残す）
 * @returns {{ [columnIndex: number]: string } | null} エラーがある列のインデックスとメッセージ。組織コード列は常に key が
 *   HIERARCHY_ORG_CODE_COLUMN_INDEX（6）で返す（表示側の統一）
 */
export function validateHierarchyRow(row, departmentList, options = {}) {
  if (!Array.isArray(row) || row.length < 2) return null
  const orgCodeColIdx = HIERARCHY_ORG_CODE_COLUMN_INDEX
  const hierarchies = row.slice(0, row.length - 1).map((v) => (v != null ? String(v).trim() : ''))
  const orgCode = (row[row.length - 1] ?? '').toString().trim()
  const hasHierarchy = hierarchies.some((v) => v !== '' && v !== '-')

  const errors = {}

  if (!orgCode) {
    errors[orgCodeColIdx] = getMessage('E020001')
  }
  if (!hasHierarchy) {
    errors[0] = getMessage('E020001')
  }

  const dept = departmentList.find((d) => (d.department_code ?? d.code ?? '').toString().trim() === orgCode)
  if (orgCode && !dept) {
    errors[orgCodeColIdx] = errors[orgCodeColIdx] || getMessage('E020004', '組織')
  } else if (orgCode && dept) {
    // master CSV の階層セルは「表示用の正式な組織名」そのもの（drilldown 記号や数値プレフィックスは含まない）。行末に近い最後の非空セルをツリー上の組織名として扱う
    const lastName = hierarchies.filter((v) => v !== '' && v !== '-')
    const bottomName = lastName[lastName.length - 1]
    const deptName = (dept.department_name ?? dept.name ?? '').toString().trim()

    // 同一階層内の重複名を避けるため batch が "(親/祖先)" サフィックスを付与する場合がある。
    // 親階層名（master の実名）を使って有効なサフィックスを列挙する
    const parentNamesInRow = lastName.length >= 2 ? lastName.slice(0, -1) : []
    const validSuffixes = new Set()
    for (let i = 0; i < parentNamesInRow.length; i++) {
      for (let j = i; j < parentNamesInRow.length; j++) {
        validSuffixes.add(parentNamesInRow.slice(i, j + 1).join('/'))
      }
    }

    const exactMatch = deptName === bottomName
    const suffixMatch =
      bottomName &&
      deptName &&
      bottomName.startsWith(deptName) &&
      bottomName.length > deptName.length &&
      (bottomName[deptName.length] === '(' || bottomName[deptName.length] === '（') &&
      (() => {
        const open = bottomName[deptName.length]
        const close = open === '（' ? '）' : ')'
        const closeIdx = bottomName.indexOf(close, deptName.length + 1)
        if (closeIdx === -1) return false
        const suffix = bottomName.slice(deptName.length + 1, closeIdx).trim()
        const rest = bottomName.slice(closeIdx + 1).trim()
        if (rest !== '') return false
        return validSuffixes.has(suffix)
      })()
    const nameMatches = exactMatch || suffixMatch

    if (bottomName && !nameMatches) {
      errors[orgCodeColIdx] = getMessage('E020005')
      let nameColIdx = -1
      for (let j = 0; j < hierarchies.length; j++) {
        if (hierarchies[j] !== '' && hierarchies[j] !== '-') nameColIdx = j
      }
      if (nameColIdx >= 0) {
        errors[nameColIdx] = getMessage('E020006')
      }
    }
  }

  return Object.keys(errors).length ? errors : null
}

/**
 * 新規期間作成の開始日バリデーション - モーダル入力用
 *
 * @param {string} startDate - YYYY-MM-DD
 * @param {string} minStartDate - 最小開始日（最新期間の開始日）
 * @param {Object[]} existingPeriods - 既存期間一覧（startDate を持つ）
 * @returns {{ start_date?: string }} エラーオブジェクト
 */
export function validateNewPeriod(startDate, minStartDate, existingPeriods = []) {
  const errors = {}
  const s = (startDate ?? '').toString().trim()

  // 開始年月日: E010001 (必須)
  if (!s) {
    errors.start_date = getMessage('E010001', '開始年月日')
    return errors
  }

  const normalized = s.replace(/-/g, '/')
  const exists = existingPeriods.some((p) => {
    const pStart = (p.startDate ?? p.StartDate ?? '').toString().trim().replace(/-/g, '/')
    return pStart === normalized
  })
  if (exists) {
    errors.start_date = 'この期間は既に存在します'
  }
  if (minStartDate) {
    const minNorm = String(minStartDate).trim().replace(/-/g, '/')
    if (normalized <= minNorm) {
      errors.start_date =
        errors.start_date ||
        '最新対象期間開始日の2日後以降の日付を選択してください（前の期間の終了日が自動で設定されます）'
    }
  }
  return errors
}
